/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2023-2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import { useAuth } from 'Contexts/Auth';
import type React from 'react';
import { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';

function Login() {
  const [params] = useSearchParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = username.trim().length > 0 && password.length > 0 && !submitting;

  async function onLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await auth.login(username.trim(), password);
      navigate(params.get('path')?.toString() || '/new/home');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400 || status === 401 || status === 404) {
        setError('Username or password is incorrect.');
      } else {
        setError('Login failed. The server could not be reached. Please try again later.');
      }
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container>
      <Row className="vh-100 d-flex justify-content-center align-items-center">
        <Col md={8} lg={6} xs={12}>
          <div className="border border-3 border-primary"></div>
          <Card className="shadow">
            <Card.Body>
              <div className="mb-3 mt-md-4">
                <h2 className="fw-bold mb-2">MsgToDb</h2>
                <p className="mb-3">Please enter your username and password.</p>

                <Form onSubmit={onLogin} noValidate>
                  <Form.Group className="mb-3" controlId="user">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      autoComplete="username"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      isInvalid={!!error}
                      autoFocus
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="password">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      isInvalid={!!error}
                    />
                  </Form.Group>

                  {auth.sessionExpired && !error && (
                    <Alert variant="info" className="py-2">
                      Your session has expired. Please log in again.
                    </Alert>
                  )}

                  {error && (
                    <Alert variant="danger" className="py-2">
                      {error}
                    </Alert>
                  )}

                  <Button variant="primary" type="submit" disabled={!canSubmit}>
                    {submitting ? 'Logging in…' : 'Login'}
                  </Button>
                </Form>

                <p className="text-muted small mt-4 mb-0">
                  Accounts are created by an administrator. If you need access or have forgotten your password, please
                  contact the umati team.
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Login;
